/**
 * Wave 10 (Frontend audit C1): generic error boundary for SPA routes.
 *
 * Pre-fix the SPA had no `errorElement` on any route — a render-time
 * crash anywhere in `Dashboard.tsx`, `ProductList.tsx`, etc., showed
 * the React default white screen with the error stack. With this
 * boundary attached, runtime errors render a friendly card with a
 * "refresh" action and the error message tucked away.
 *
 * Used by `router.tsx` as `errorElement: <RouteErrorBoundary />`.
 */
import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import { isStaleChunkError, reloadOnceForStaleChunk } from '../../utils/staleChunk';

export function RouteErrorBoundary() {
  const err = useRouteError();

  const staleChunk = isStaleChunkError(err);

  useEffect(() => {
    // A chunk from the previous deploy is gone (tab opened before a release):
    // reload once to pick up the new build instead of reporting + dead-ending.
    if (staleChunk && reloadOnceForStaleChunk()) return;
    // A stale chunk right after a deploy is expected, not a defect: show the reload
    // prompt without reporting it (the reload may already be in flight).
    if (err && !staleChunk) {
      // Monitoring is optional and should not inflate the critical portal
      // bundle. The recovery screen still works if the chunk cannot load.
      void import('@sentry/react')
        .then(({ captureException }) => captureException(err))
        .catch(() => undefined);
    }
  }, [err, staleChunk]);

  const isErrorObj = err instanceof Error;
  const message = staleChunk
    ? 'This portal was just updated. Reload the page to continue.'
    : isErrorObj
    ? err.message
    : typeof err === 'string'
      ? err
      : 'Something went wrong while loading this page.';

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="w-full max-w-md rounded-xl border border-red-200 bg-white p-6 text-center shadow-sm dark:border-red-900 dark:bg-gray-900">
        <h1 className="mb-2 text-xl font-semibold text-red-700 dark:text-red-400">
          Something went wrong
        </h1>
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          {message}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
        >
          Reload page
        </button>
        <p className="mt-3 text-xs text-gray-400">
          If this keeps happening, contact your marketplace owner.
        </p>
      </div>
    </div>
  );
}
